import React, { useState } from 'react';
import {
  FiX as X,
  FiMessageSquare as Slack,
  FiCheck as Check,
  FiRefreshCw as RefreshCw,
  FiSend as Send,
  FiAlertCircle as AlertCircle,
} from 'react-icons/fi';

interface SlackExistingConfig {
  workspace?: string;
  channel?: string;
  webhookUrl?: string;
}

interface SlackConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (config: { workspace: string; channel: string; webhookUrl: string; sendTest: boolean }) => void;
  existingConfig?: SlackExistingConfig | null;
  saving: boolean;
}

/**
 * Modal for configuring Slack integration.
 * Allows users to set up webhook URL, workspace, and channel.
 */
const SlackConfigModal: React.FC<SlackConfigModalProps> = ({ isOpen, onClose, onSave, existingConfig, saving }) => {
  const [workspace, setWorkspace] = useState(existingConfig?.workspace || '');
  const [channel, setChannel] = useState(existingConfig?.channel || '');
  const [webhookUrl, setWebhookUrl] = useState(existingConfig?.webhookUrl || '');
  const [sendTest] = useState(false);
  const [testSent, setTestSent] = useState(false);
  const [testError, setTestError] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);

  if (!isOpen) return null;

  const handleSendTest = async () => {
    if (!webhookUrl) return;

    setTesting(true);
    setTestError(null);
    setTestSent(false);

    try {
      // In a real implementation, this would call an API endpoint
      // that sends a test message to the Slack webhook
      await new Promise(resolve => setTimeout(resolve, 1000));
      setTestSent(true);
    } catch (err) {
      setTestError('Failed to send test notification');
    } finally {
      setTesting(false);
    }
  };

  const handleSave = () => {
    onSave({
      workspace,
      channel,
      webhookUrl,
      sendTest,
    });
  };

  const isValid = webhookUrl?.startsWith('https://hooks.slack.com/');

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-75 transition-opacity"
          onClick={onClose} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClose(); }} role="presentation"
        />
        {/* Modal */}
        <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
          {/* Header */}
          <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30">
                  <Slack className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Connect Slack
                </h3>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-4 space-y-5">
            {/* Info Banner */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <p className="text-sm text-blue-800 dark:text-blue-300">
                To connect Slack, you need to create an Incoming Webhook in your Slack workspace.{' '}
                <a
                  href="https://api.slack.com/messaging/webhooks"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline font-medium"
                >
                  Learn how
                </a>
              </p>
            </div>

            {/* Workspace */}
            <div className="space-y-2">
              <label htmlFor="workspace-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Workspace Name
              </label>
              <input id="workspace-name"
                type="text"
                value={workspace}
                onChange={(e) => setWorkspace(e.target.value)}
                placeholder="your-company"
                className="w-full px-4 py-2.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400">
                e.g., your-company (from your-company.slack.com)
              </p>
            </div>

            {/* Channel */}
            <div className="space-y-2">
              <label htmlFor="channel" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Channel
              </label>
              <input id="channel"
                type="text"
                value={channel}
                onChange={(e) => setChannel(e.target.value)}
                placeholder="#alerts"
                className="w-full px-4 py-2.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400">
                The channel where notifications will be posted
              </p>
            </div>

            {/* Webhook URL */}
            <div className="space-y-2">
              <label htmlFor="webhook-url" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Webhook URL <span className="text-red-500">*</span>
              </label>
              <input id="webhook-url"
                type="url"
                value={webhookUrl}
                onChange={(e) => setWebhookUrl(e.target.value)}
                placeholder="https://hooks.slack.com/services/..."
                className="w-full px-4 py-2.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              {webhookUrl && !isValid && (
                <p className="text-xs text-red-500 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  Must be a valid Slack webhook URL
                </p>
              )}
            </div>

            {/* Test Notification */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Test Connection
                </span>
                <button
                  onClick={handleSendTest}
                  disabled={!isValid || testing}
                  className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {testing ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Send Test
                    </>
                  )}
                </button>
              </div>
              {testSent && (
                <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                  <Check className="w-4 h-4" />
                  Test notification sent successfully
                </div>
              )}
              {testError && (
                <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
                  <AlertCircle className="w-4 h-4" />
                  {testError}
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!isValid || saving}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Connecting...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  Connect Slack
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SlackConfigModal;
